/**
 * Iframe Telemetry - Track and monitor widget loads from external domains
 *
 * Note: This is for telemetry/monitoring only. Security is enforced by
 * CSP frame-ancestors (server-side) which controls which origins can embed the widget.
 */
import * as Sentry from "@sentry/react";

/**
 * Check if we're running in an iframe
 */
function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true; // Blocked by same-origin policy = definitely in iframe
  }
}

/**
 * Get the parent domain hosting the iframe
 */
function getParentDomain(): string {
  try {
    // In development, if we can access parent directly, we're same-origin
    if (import.meta.env.DEV) {
      try {
        // If we can access parent.location, we're same-origin (likely localhost)
        if (window.parent?.location && window.parent !== window) {
          return new URL(window.parent.location.href).hostname;
        }
      } catch {
        // Cross-origin, continue with normal detection
      }
    }

    // Try to get parent domain
    if (window.location.ancestorOrigins?.length > 0) {
      return new URL(window.location.ancestorOrigins[0]).hostname;
    }
    // Fallback to document.referrer
    if (document.referrer) {
      return new URL(document.referrer).hostname;
    }
    return "unknown";
  } catch {
    return "unknown";
  }
}

/**
 * Initialize iframe telemetry monitoring
 */
export function initializeIframeTelemetry(): void {
  // Only run if in iframe
  if (!isInIframe()) {
    return;
  }

  const parentDomain = getParentDomain();

  // Track all iframe loads as breadcrumb for debugging
  Sentry.addBreadcrumb({
    category: "iframe.load",
    message: `Widget loaded from: ${parentDomain}`,
    level: "info",
    data: { domain: parentDomain },
  });
}
